import React, { ChangeEvent, useEffect, useRef, useState, useCallback } from 'react';
import { Box, HStack, Flex, Collapse } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import partial from 'lodash-es/partial';
import { useAddNewAutomation } from 'pages/Automations/hooks';
import {
  AutomationsListItemFragment,
  useGetAutomationsSelectionListLazyQuery,
  useToggleManyAutomationPublishedStateMutation,
} from 'shared/types/graphql';
import { getAutomationsListQuery } from 'pages/Automations/constants/operationNames';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { validateAutomation } from 'pages/Automations/utils/validation/validateAutomation';
import { convertAutomationPersistedToDraft } from 'pages/Automations/state/utils/draft';

interface AutomationsListHeaderProps {
  filteredAutomationsList: AutomationsListItemFragment[];
  isSelectingAutomations: boolean;
  selectedAutomationIds: Set<string>;
  searchInputValue: string;
  onToggleSelectionMode: () => void;
  setSearchInputValue: React.Dispatch<React.SetStateAction<string>>;
  onSelectAutomations: (ids: string[]) => void;
  onSetAutomationListErrors: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const AutomationsListHeader = ({
  filteredAutomationsList,
  isSelectingAutomations,
  selectedAutomationIds,
  searchInputValue,
  onToggleSelectionMode,
  setSearchInputValue,
  onSelectAutomations,
  onSetAutomationListErrors,
}: AutomationsListHeaderProps): JSX.Element => {
  const { isImpersonating } = useAuth();
  const { currentClinicId } = useClinicUser();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false);
  const { handleAddNewAutomation } = useAddNewAutomation();

  const handleSearchButtonClick = (): void => {
    setIsSearchInputVisible((prevState) => !prevState);
    setSearchInputValue('');
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchInputValue(e.target.value);
  };

  useEffect(() => {
    if (isSearchInputVisible && searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchInputVisible, searchInputRef]);

  const toast = useToast();

  const [isUpdatingManyAutomationPublishedState, setIsUpdatingManyAutomationPublishedState] = useState(false);
  const [updateManyAutomationPublishedState] = useToggleManyAutomationPublishedStateMutation({
    refetchQueries: [getAutomationsListQuery],
    awaitRefetchQueries: true,
    onCompleted: (data, options) => {
      const count = data.bulkPublishWorkflowEventSettings?.count;
      if (count && options?.variables) {
        const isPlural = count > 1;
        toast({
          status: 'success',
          title: `Automation${isPlural ? 's' : ''} ${options.variables.isPublished ? 'activated' : 'deactivated'}`,
          description: `The ${selectedAutomationIds.size} selected automation${isPlural ? 's are' : ' is'} now ${
            options.variables.isPublished ? 'active' : 'inactive'
          }.`,
        });
      }
    },
    onError: (error, options) => {
      if (options?.variables) {
        const count = options.variables.workflowEventSettingIds?.length ?? 0;
        const isPlural = count !== 1;
        toast({
          status: 'error',
          title: `Whoops!`,
          description: `There was an error while trying to ${
            options.variables.isPublished ? 'activate' : 'deactivate'
          } ${count} automation${isPlural ? 's' : ''}. Please try again.`,
        });
      }
    },
  });

  const [getAutomationsSelectionList] = useGetAutomationsSelectionListLazyQuery({
    variables: {
      clinicId: currentClinicId || '',
    },
  });

  const onToggleAutomationsPublishedStateClick = useCallback(
    async (isPublished: boolean) => {
      setIsUpdatingManyAutomationPublishedState(true);
      if (isPublished) {
        const automations = (await getAutomationsSelectionList())?.data?.findManyWorkflowEventSetting;
        const invalidAutomationIds =
          automations
            ?.filter((automation) => {
              if (selectedAutomationIds.has(automation.id)) {
                return !validateAutomation(
                  convertAutomationPersistedToDraft({ automation, conditionSets: automation.conditionSets }),
                ).isValid;
              }
              return false;
            })
            .map(({ id }) => id) || [];

        if (!!invalidAutomationIds.length) {
          onSetAutomationListErrors(new Set(invalidAutomationIds));
          const isPlural = invalidAutomationIds.length > 1;
          toast({
            status: 'error',
            title: `Whoops!`,
            description: `${isPlural ? 'Some' : 'A'} selected automation${
              isPlural ? 's' : ''
            } can't be activated because ${
              isPlural ? 'they are' : 'it is'
            } invalid. Please fix and save the invalid automation${
              invalidAutomationIds.length > 1 ? 's' : ''
            } and try again.`,
          });
          setIsUpdatingManyAutomationPublishedState(false);
          return;
        }
      }

      onSetAutomationListErrors(new Set());

      await updateManyAutomationPublishedState({
        variables: { workflowEventSettingIds: Array.from(selectedAutomationIds), isPublished },
      });
      setIsUpdatingManyAutomationPublishedState(false);
    },
    [
      selectedAutomationIds,
      toast,
      updateManyAutomationPublishedState,
      getAutomationsSelectionList,
      onSetAutomationListErrors,
    ],
  );

  const onSelectAutomationsCheckboxChange = useCallback((): void => {
    if (selectedAutomationIds.size === filteredAutomationsList.length) {
      onSelectAutomations([]);
    } else {
      onSelectAutomations(filteredAutomationsList.map((automation) => automation.id));
    }
  }, [filteredAutomationsList, onSelectAutomations, selectedAutomationIds.size]);

  return (
    <Box
      className="Automations__ListHeader"
      flex="0 0 auto"
      w="100%"
      alignItems="stretch"
      p={2}
      borderBottom="1px solid"
      borderColor="border.default"
    >
      <HStack w="100%" justifyContent="space-between">
        <Flex align="center">
          {isImpersonating && (
            <Button
              variant="ghostNeutral"
              iconName="bulkSelect"
              sx={{
                ['& svg']: {
                  color: isSelectingAutomations ? 'border.info' : 'unset',
                },
              }}
              onClick={onToggleSelectionMode}
            />
          )}
          <Box className="Automations__ListHeader-SearchButton">
            <Button
              onClick={handleSearchButtonClick}
              variant="ghostNeutral"
              iconName="magnifyingGlass"
              sx={{
                ['& svg']: {
                  color: isSearchInputVisible ? 'border.info' : 'unset',
                },
              }}
            />
          </Box>
        </Flex>
        <Box className="Automations__ListHeader-AddNewButton">
          {isSelectingAutomations ? (
            <HStack>
              <Button
                isDisabled={!selectedAutomationIds.size}
                isLoading={isUpdatingManyAutomationPublishedState}
                variant="primary"
                size="sm"
                onClick={partial(onToggleAutomationsPublishedStateClick, true)}
              >
                Activate
              </Button>
              <Button
                isDisabled={!selectedAutomationIds.size}
                isLoading={isUpdatingManyAutomationPublishedState}
                variant="secondary"
                size="sm"
                onClick={partial(onToggleAutomationsPublishedStateClick, false)}
              >
                Deactivate
              </Button>
            </HStack>
          ) : (
            <Button variant="ghost" onClick={handleAddNewAutomation} iconName="plus" size="sm">
              Add New
            </Button>
          )}
        </Box>
      </HStack>
      <Box w="100%">
        <Collapse in={isSearchInputVisible} animateOpacity>
          <Box p={1} mt={2}>
            <TextInput
              onChange={handleSearchInputChange}
              value={searchInputValue}
              placeholder="Search by automation name"
              ref={searchInputRef}
              className="Automations__ListHeader-SearchInput"
              onClear={(): void => setSearchInputValue('')}
              isClearable={true}
            />
          </Box>
        </Collapse>
      </Box>
      {isSelectingAutomations && (
        <Flex align="center" justify="space-between" bg="background.info" p={2} mt={2} borderRadius="md">
          <HStack align="center" p={1}>
            <Checkbox
              id="select-all-automations-checkbox"
              isChecked={selectedAutomationIds.size === filteredAutomationsList.length}
              isIndeterminate={
                selectedAutomationIds.size !== 0 && selectedAutomationIds.size !== filteredAutomationsList.length
              }
              onChange={onSelectAutomationsCheckboxChange}
            />
            <Box flex=" 0 0 auto">
              <label htmlFor="select-all-automations-checkbox" style={{ cursor: 'pointer' }}>
                {selectedAutomationIds.size} selected
              </label>
            </Box>
          </HStack>

          <Button iconName="close" size="sm" variant="ghostNeutral" onClick={onToggleSelectionMode} />
        </Flex>
      )}
    </Box>
  );
};
